import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import api, { getAll } from '../../services/api';
import { notify } from '../../utils/notifications';
import authStore from '../../store/authStore';
import { puoModificare } from '../../utils/permessi';
import {
  ArrowLeft, Plus, Search, X, Edit, Trash2, Mail, Phone, MapPin, FileText
} from 'lucide-react';

const TIPI = {
  SCUOLA: 'Scuola o istituto',
  AZIENDA: 'Azienda',
  ASSOCIAZIONE: 'Associazione',
  PARROCCHIA: 'Parrocchia',
  ENTE: 'Ente pubblico',
  PRIVATO: 'Privato',
  ALTRO: 'Altro',
};

const INPUT = 'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1b94ce]';

const vuoto = {
  denominazione: '', tipo: 'SCUOLA', referente: '', email: '', telefono: '',
  indirizzo: '', citta: '', provincia: '', cap: '',
  partita_iva: '', codice_fiscale: '', codice_destinatario: '',
  note: '', attivo: true,
};

const Campo = ({ etichetta, children, span }) => (
  <div className={span ? 'sm:col-span-2' : ''}>
    <label className="block text-[11px] font-medium text-gray-500 mb-1">{etichetta}</label>
    {children}
  </div>
);

const Committenti = () => {
  const navigate = useNavigate();
  const { utente } = authStore();
  const modificabile = puoModificare(utente);

  const [committenti, setCommittenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ricerca, setRicerca] = useState('');
  const [modale, setModale] = useState(null);

  const carica = useCallback(async () => {
    setLoading(true);
    try {
      setCommittenti(await getAll('/preventivi/committenti/'));
    } catch (error) {
      console.error('Errore caricamento committenti:', error);
      notify.error('Errore nel caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carica(); }, [carica]);

  const elimina = async (c) => {
    if (!window.confirm(`Eliminare ${c.denominazione}?`)) return;
    const toastId = notify.loading('Eliminazione...');
    try {
      await api.delete(`/preventivi/committenti/${c.id}/`);
      notify.update(toastId, { type: 'success', render: 'Committente eliminato' });
      carica();
    } catch (error) {
      notify.update(toastId, {
        type: 'error',
        render: error.response?.data?.errore || 'Errore nell\'eliminazione',
      });
    }
  };

  const testo = ricerca.trim().toLowerCase();
  const filtrati = committenti.filter((c) => !testo || [
    c.denominazione, c.referente, c.email, c.telefono, c.citta, c.partita_iva,
  ].some((v) => (v || '').toLowerCase().includes(testo)));

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin/preventivi')}
              className="p-2 hover:bg-gray-100 rounded-lg transition"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Committenti</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Chi richiede i preventivi: scuole, aziende, associazioni, privati
              </p>
            </div>
          </div>
          {modificabile && (
            <button
              onClick={() => setModale({ ...vuoto })}
              className="flex items-center gap-2 px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 transition font-medium text-sm"
            >
              <Plus className="w-4 h-4" />
              Nuovo committente
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 mb-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              placeholder="Denominazione, referente, città..."
              className="w-full pl-9 pr-9 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1b94ce]"
            />
            {ricerca && (
              <button onClick={() => setRicerca('')}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-900">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
          <span className="text-sm text-gray-500">{filtrati.length}</span>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" />
            </div>
          ) : filtrati.length === 0 ? (
            <div className="py-16 text-center">
              <p className="text-sm text-gray-500">
                {committenti.length === 0 ? 'Nessun committente ancora' : 'Nessun risultato'}
              </p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {filtrati.map((c) => (
                <div key={c.id} className="px-4 py-3 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900 truncate">
                      {c.denominazione}
                      {!c.attivo && (
                        <span className="ml-2 px-2 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded-full">
                          non attivo
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-gray-500 truncate flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-0.5">
                      <span>{TIPI[c.tipo] || c.tipo}</span>
                      {c.referente && <span>{c.referente}</span>}
                      {c.email && (
                        <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>
                      )}
                      {c.telefono && (
                        <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.telefono}</span>
                      )}
                      {c.citta && (
                        <span className="flex items-center gap-1"><MapPin className="w-3 h-3" />{c.citta}</span>
                      )}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    {c.numero_preventivi > 0 && (
                      <span
                        title={`${c.numero_preventivi} preventivi`}
                        className="hidden sm:flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold bg-gray-100 text-gray-600 rounded-full"
                      >
                        <FileText className="w-3 h-3" />
                        {c.numero_preventivi}
                      </span>
                    )}
                    {modificabile && (
                      <>
                        <button onClick={() => setModale(c)} title="Modifica"
                          className="p-1.5 text-gray-400 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition">
                          <Edit className="w-4 h-4" />
                        </button>
                        <button onClick={() => elimina(c)} title="Elimina"
                          className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {modale && (
        <ModaleCommittente
          iniziale={modale}
          onClose={() => setModale(null)}
          onSalvato={() => { setModale(null); carica(); }}
        />
      )}
    </AdminLayout>
  );
};

const ModaleCommittente = ({ iniziale, onClose, onSalvato }) => {
  const [dati, setDati] = useState(iniziale);
  const [salvataggio, setSalvataggio] = useState(false);
  const modifica = Boolean(iniziale.id);

  const c = (campo, valore) => setDati((d) => ({ ...d, [campo]: valore }));

  const salva = async (e) => {
    e.preventDefault();
    setSalvataggio(true);
    const toastId = notify.loading('Salvataggio...');
    try {
      if (modifica) {
        await api.put(`/preventivi/committenti/${dati.id}/`, dati);
      } else {
        await api.post('/preventivi/committenti/', dati);
      }
      notify.update(toastId, { type: 'success', render: 'Committente salvato' });
      onSalvato();
    } catch (error) {
      const d = error.response?.data;
      notify.update(toastId, {
        type: 'error',
        render: d ? Object.entries(d).map(([k, v]) => `${k}: ${Array.isArray(v) ? v[0] : v}`).join(' · ') : 'Errore',
      });
    } finally {
      setSalvataggio(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white rounded-lg shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="border-b border-gray-200 px-4 py-3 flex justify-between items-center sticky top-0 bg-white">
          <h2 className="text-base font-bold text-gray-900">
            {modifica ? 'Modifica committente' : 'Nuovo committente'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={salva} className="p-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Campo etichetta="Denominazione *" span>
            <input type="text" required value={dati.denominazione}
              onChange={(e) => c('denominazione', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Tipo">
            <select value={dati.tipo} onChange={(e) => c('tipo', e.target.value)} className={INPUT}>
              {Object.entries(TIPI).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
          </Campo>
          <Campo etichetta="Referente">
            <input type="text" value={dati.referente || ''} onChange={(e) => c('referente', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Email">
            <input type="email" value={dati.email || ''} onChange={(e) => c('email', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Telefono">
            <input type="text" value={dati.telefono || ''} onChange={(e) => c('telefono', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Indirizzo" span>
            <input type="text" value={dati.indirizzo || ''} onChange={(e) => c('indirizzo', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="CAP">
            <input type="text" value={dati.cap || ''} onChange={(e) => c('cap', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Città">
            <input type="text" value={dati.citta || ''} onChange={(e) => c('citta', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Provincia">
            <input type="text" maxLength={5} value={dati.provincia || ''}
              onChange={(e) => c('provincia', e.target.value.toUpperCase())} className={INPUT} />
          </Campo>
          <Campo etichetta="Partita IVA">
            <input type="text" value={dati.partita_iva || ''} onChange={(e) => c('partita_iva', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Codice fiscale">
            <input type="text" value={dati.codice_fiscale || ''} onChange={(e) => c('codice_fiscale', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Codice destinatario">
            <input type="text" value={dati.codice_destinatario || ''}
              onChange={(e) => c('codice_destinatario', e.target.value)} className={INPUT} />
          </Campo>
          <Campo etichetta="Note" span>
            <textarea rows={2} value={dati.note || ''} onChange={(e) => c('note', e.target.value)} className={INPUT} />
          </Campo>
          <div className="sm:col-span-2 flex items-center gap-2">
            <input type="checkbox" id="attivo" checked={dati.attivo}
              onChange={(e) => c('attivo', e.target.checked)} className="w-4 h-4 accent-[#1b94ce]" />
            <label htmlFor="attivo" className="text-sm text-gray-700">Attivo</label>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-2 pt-2 border-t border-gray-200">
            <button type="button" onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-semibold hover:bg-gray-50">
              Annulla
            </button>
            <button type="submit" disabled={salvataggio}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-semibold hover:bg-gray-800 disabled:opacity-50">
              {salvataggio ? 'Salvataggio...' : 'Salva'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Committenti;
